import { useCallback, useEffect, useMemo, useState } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Copy, Gift, Loader2, Plus, Rocket, Trash2} from 'lucide-react'
import WizardJornadaCompartilhe, { type AcessoCompartilhe } from '@/components/WizardJornadaCompartilhe'
import { copiarTexto } from '@/utils/clipboard'
import {
  gerarTokenCompartilhe,
  linkDiagnosticoConsultora,
  linkPublicoCompartilhe,
  rotuloStatusAcesso,
} from '@/utils/jornadaCompartilhe'

interface ConsultoraSessao {
  _id?: string
  nome?: string
  usuario?: string
  email?: string
  metaMensalCompartilhe?: string
}

interface Props {
  consultoraAdmin: string
  isAdmin?: boolean
}

function slugConsultora(): string {
  try {
    const raw = localStorage.getItem('admin2_sessao')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { tipo?: string; usuario?: string }
    if (parsed?.tipo === 'consultora' && parsed.usuario) return String(parsed.usuario)
  } catch {
    /* ignore */
  }
  return ''
}

export default function AbaComoCrescer({ consultoraAdmin, isAdmin = false }: Props) {
  const slug = (isAdmin ? consultoraAdmin : slugConsultora() || consultoraAdmin || '').trim()
  const [consultora, setConsultora] = useState<ConsultoraSessao | null>(null)
  const [acessos, setAcessos] = useState<AcessoCompartilhe[]>([])
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [acessoAtivo, setAcessoAtivo] = useState<AcessoCompartilhe | null>(null)
  const [nomePresente, setNomePresente] = useState('')
  const [emailPresente, setEmailPresente] = useState('')
  const [metaMensal, setMetaMensal] = useState('')
  const [salvandoMeta, setSalvandoMeta] = useState(false)

  const carregar = useCallback(async () => {
    if (!slug) {
      setCarregando(false)
      return
    }
    setCarregando(true)
    try {
      const [resConsultora, resAcessos] = await Promise.all([
        lumi.entities.consultoras.list({ filter: { usuario: slug }, limit: 1 }),
        lumi.entities.jornada_compartilhe_acessos.list({
          filter: { consultora: slug },
          sort: { createdAt: -1 },
          limit: 200,
        }),
      ])
      const atual = (resConsultora?.list?.[0] || null) as ConsultoraSessao | null
      setConsultora(atual)
      setMetaMensal(String(atual?.metaMensalCompartilhe || ''))
      setAcessos((resAcessos?.list || []) as AcessoCompartilhe[])
    } catch (e) {
      console.error('[como-crescer] erro ao carregar', e)
      toast.error('Erro ao carregar a Jornada Compartilhe')
    } finally {
      setCarregando(false)
    }
  }, [slug])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const concluidas = useMemo(
    () => acessos.filter((a) => a.status === 'concluido' || a.etapaAtual === 'concluido').length,
    [acessos],
  )

  async function criarAcesso(params: {
    nomeDestinatario: string
    emailDestinatario: string
    origem: 'consultora' | 'presente' | 'admin'
  }) {
    if (!slug) {
      toast.error('Sessão da consultora não encontrada.')
      return
    }
    const nome = params.nomeDestinatario.trim()
    const email = params.emailDestinatario.trim().toLowerCase()
    if (!nome || !email) {
      toast.error('Informe nome e e-mail.')
      return
    }
    setSalvando(true)
    try {
      const agora = new Date().toISOString()
      const criado = (await lumi.entities.jornada_compartilhe_acessos.create({
        token: gerarTokenCompartilhe(),
        nomeDestinatario: nome,
        emailDestinatario: email,
        consultora: slug,
        origem: params.origem,
        status: 'ativo',
        etapaAtual: 'contatos',
        contatosJson: '[]',
        formatoEscolhido: '',
        cronogramaJson: '',
        linkDiagnosticoGratuito: linkDiagnosticoConsultora(slug),
        historiaRespostasJson: '',
        roteiroHistoria: '',
        creator: slug,
        creatorEmail: consultora?.email || email,
        createdAt: agora,
        updatedAt: agora,
      })) as AcessoCompartilhe
      toast.success('Jornada Compartilhe criada.')
      setNomePresente('')
      setEmailPresente('')
      await carregar()
      if (params.origem !== 'presente') {
        setAcessoAtivo(criado)
      }
    } catch (e) {
      console.error('[como-crescer] erro ao criar acesso', e)
      toast.error('Não foi possível criar a jornada')
    } finally {
      setSalvando(false)
    }
  }

  async function salvarMeta() {
    if (!consultora?._id) return
    setSalvandoMeta(true)
    try {
      await lumi.entities.consultoras.update(consultora._id, {
        metaMensalCompartilhe: metaMensal.trim(),
        updatedAt: new Date().toISOString(),
      })
      toast.success('Meta mensal salva.')
    } catch (e) {
      console.error('[como-crescer] erro ao salvar meta', e)
      toast.error('Não foi possível salvar a meta')
    } finally {
      setSalvandoMeta(false)
    }
  }

  async function copiarLink(token: string) {
    try {
      await copiarTexto(linkPublicoCompartilhe(token))
      toast.success('Link copiado!')
    } catch {
      toast.error('Não foi possível copiar o link.')
    }
  }

  async function excluirAcesso(acesso: AcessoCompartilhe) {
    if (!acesso._id) return
    if (!window.confirm(`Excluir a jornada de ${acesso.nomeDestinatario}?`)) return
    try {
      await lumi.entities.jornada_compartilhe_acessos.delete(acesso._id)
      toast.success('Jornada excluída.')
      if (acessoAtivo?._id === acesso._id) setAcessoAtivo(null)
      await carregar()
    } catch (e) {
      console.error('[como-crescer] erro ao excluir', e)
      toast.error('Erro ao excluir')
    }
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16 text-[#5F5E5A]">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando Jornada Compartilhe...
      </div>
    )
  }

  if (acessoAtivo) {
    return (
      <WizardJornadaCompartilhe
        acesso={acessoAtivo}
        onVoltar={() => {
          setAcessoAtivo(null)
          void carregar()
        }}
        onAtualizado={(novo) => {
          setAcessoAtivo(novo)
          setAcessos((prev) => prev.map((item) => (item._id === novo._id ? novo : item)))
        }}
      />
    )
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-1 flex items-center gap-2">
          <Rocket className="w-6 h-6 text-[#2D6A4F]" />
          Jornada Compartilhe
        </h2>
        <p className="text-sm text-[#5F5E5A] leading-relaxed">
          Ferramenta gratuita e ilimitada para planejar conversas, copiar mensagens prontas e acompanhar 14 dias de toques.
          Sem créditos, sem IA e sem limite de uso.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-[#EDE0D4] shadow-sm p-5">
          <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-1">Suas jornadas</p>
          <p className="text-2xl font-cormorant font-semibold text-[#2D6A4F]">
            Você já completou {concluidas} {concluidas === 1 ? 'Jornada Compartilhe' : 'Jornadas Compartilhe'}
          </p>
          <p className="text-xs text-[#5F5E5A] mt-1">{acessos.length} criada(s) no total</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#EDE0D4] shadow-sm p-5">
          <label className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-2 block">
            Meta mensal (opcional)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={metaMensal}
              onChange={(e) => setMetaMensal(e.target.value)}
              placeholder="Ex: 8 conversas neste mês"
              className="flex-1 px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-sm"
            />
            <button
              type="button"
              onClick={() => void salvarMeta()}
              disabled={salvandoMeta || !consultora?._id}
              className="px-4 py-2 bg-[#2D6A4F] text-white rounded-full text-sm font-medium disabled:opacity-60"
            >
              {salvandoMeta ? '...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#EDE0D4] shadow-sm p-6 mb-6">
        <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-2">Começar uma nova jornada</h3>
        <p className="text-sm text-[#5F5E5A] mb-4">
          Use para você mesma, quantas vezes quiser — inclusive repetindo todo mês.
        </p>
        <button
          type="button"
          onClick={() =>
            void criarAcesso({
              nomeDestinatario: consultora?.nome || slug,
              emailDestinatario: consultora?.email || `${slug}@descubraseusoleos.com.br`,
              origem: isAdmin ? 'admin' : 'consultora',
            })
          }
          disabled={salvando}
          className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-opacity-90 disabled:opacity-60"
        >
          <Plus className="w-4 h-4" />
          {salvando ? 'Criando...' : 'Começar nova Jornada Compartilhe'}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EDE0D4] shadow-sm p-6 mb-6">
        <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-2 flex items-center gap-2">
          <Gift className="w-5 h-5" />
          Presentear equipe ou cliente
        </h3>
        <p className="text-sm text-[#5F5E5A] mb-4">
          Cadastre nome e e-mail. O sistema gera um link único, no mesmo padrão da Jornada 12 Emoções, sem consumir crédito.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <input
            type="text"
            value={nomePresente}
            onChange={(e) => setNomePresente(e.target.value)}
            placeholder="Nome"
            className="px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-sm"
          />
          <input
            type="email"
            value={emailPresente}
            onChange={(e) => setEmailPresente(e.target.value)}
            placeholder="E-mail"
            className="px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() =>
            void criarAcesso({
              nomeDestinatario: nomePresente,
              emailDestinatario: emailPresente,
              origem: 'presente',
            })
          }
          disabled={salvando}
          className="inline-flex items-center gap-2 border-2 border-[#2D6A4F] text-[#2D6A4F] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#D8F3DC] disabled:opacity-60"
        >
          Gerar link de presente
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EDE0D4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#EDE0D4]">
          <h3 className="text-lg font-cormorant font-semibold text-[#2C2C2A]">Pessoas cadastradas / presenteadas</h3>
        </div>
        {acessos.length === 0 ? (
          <div className="p-8 text-sm text-[#5F5E5A] text-center">Nenhuma Jornada Compartilhe criada ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F8F4EF]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Pessoa</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Link</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EDE0D4]">
                {acessos.map((acesso) => {
                  const status = rotuloStatusAcesso(acesso.status, acesso.etapaAtual)
                  return (
                    <tr key={acesso._id} className="hover:bg-[#F8F4EF]">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-[#2C2C2A]">{acesso.nomeDestinatario}</div>
                        <div className="text-xs text-[#5F5E5A]">{acesso.emailDestinatario}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            status === 'Concluída' ? 'bg-[#D8F3DC] text-[#2D6A4F]' : 'bg-[#FEF3C7] text-[#92400E]'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => void copiarLink(acesso.token)}
                          className="inline-flex items-center gap-1 text-xs text-[#2D6A4F] hover:underline"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copiar link
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAcessoAtivo(acesso)}
                            className="px-3 py-1.5 text-xs font-medium bg-[#D8F3DC] text-[#2D6A4F] rounded-lg"
                          >
                            Abrir
                          </button>
                          <button
                            type="button"
                            onClick={() => void excluirAcesso(acesso)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
